import { useState, useEffect } from 'preact/hooks';

// Helper to get auth headers
function getAuthHeaders() {
  const sessionId = localStorage.getItem('sessionId');
  return sessionId ? { 'X-Session-Id': sessionId } : {};
}

export function SuperAdminWorkspaceDetail({ id }) {
  const [workspace, setWorkspace] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Form states
  const [extendDays, setExtendDays] = useState(7);
  const [discountPercent, setDiscountPercent] = useState(0);
  const [discountReason, setDiscountReason] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    fetchWorkspace();
    fetchEvents();
  }, [id]);

  async function fetchWorkspace() {
    try {
      const res = await fetch(`/api/super-admin/workspaces/${id}`, {
        headers: getAuthHeaders()
      });
      if (!res.ok) throw new Error('Failed to fetch workspace');

      const data = await res.json();
      setWorkspace(data);
      setNotes(data.notes || '');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function fetchEvents() {
    try {
      const res = await fetch(`/api/super-admin/events?workspace_id=${id}&limit=20`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setEvents(data || []);
      }
    } catch (err) {
      console.error('Failed to fetch events:', err);
    }
  }

  async function handleExtendTrial() {
    if (!extendDays || extendDays < 1) return;

    setActionLoading(true);
    try {
      const res = await fetch(`/api/super-admin/workspaces/${id}/extend-trial`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ days: extendDays })
      });

      if (!res.ok) throw new Error('Failed to extend trial');

      await fetchWorkspace();
      await fetchEvents();
      setExtendDays(7);
    } catch (err) {
      alert(err.message);
    } finally {
      setActionLoading(false);
    }
  }

  async function handleActivate() {
    if (!confirm('Manually activate this subscription for 12 months?')) return;

    setActionLoading(true);
    try {
      const res = await fetch(`/api/super-admin/workspaces/${id}/activate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ months: 12 })
      });

      if (!res.ok) throw new Error('Failed to activate');

      await fetchWorkspace();
      await fetchEvents();
    } catch (err) {
      alert(err.message);
    } finally {
      setActionLoading(false);
    }
  }

  async function handleCancel() {
    if (!confirm('Are you sure you want to cancel this subscription?')) return;

    setActionLoading(true);
    try {
      const res = await fetch(`/api/super-admin/workspaces/${id}/cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ reason: 'Cancelled by super admin' })
      });

      if (!res.ok) throw new Error('Failed to cancel');

      await fetchWorkspace();
      await fetchEvents();
    } catch (err) {
      alert(err.message);
    } finally {
      setActionLoading(false);
    }
  }

  async function handleApplyDiscount() {
    if (discountPercent < 0 || discountPercent > 100) {
      alert('Discount must be between 0 and 100%');
      return;
    }

    setActionLoading(true);
    try {
      const res = await fetch(`/api/super-admin/workspaces/${id}/discount`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          discount_percent: discountPercent,
          reason: discountReason
        })
      });

      if (!res.ok) throw new Error('Failed to apply discount');

      await fetchWorkspace();
      await fetchEvents();
      setDiscountPercent(0);
      setDiscountReason('');
    } catch (err) {
      alert(err.message);
    } finally {
      setActionLoading(false);
    }
  }

  async function handleUpdateNotes() {
    setActionLoading(true);
    try {
      const res = await fetch(`/api/super-admin/workspaces/${id}/subscription`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ notes })
      });

      if (!res.ok) throw new Error('Failed to update notes');

      await fetchWorkspace();
    } catch (err) {
      alert(err.message);
    } finally {
      setActionLoading(false);
    }
  }

  if (loading) {
    return (
      <div class="super-admin-page">
        <div class="loading-state">
          <div class="spinner" />
          <p>Loading workspace...</p>
        </div>
      </div>
    );
  }

  if (error || !workspace) {
    return (
      <div class="super-admin-page">
        <div class="error-state">
          <h2>Error</h2>
          <p>{error || 'Workspace not found'}</p>
          <a href="/super-admin/workspaces" class="btn btn-primary">Back to Workspaces</a>
        </div>
      </div>
    );
  }

  const sub = workspace.subscription;

  return (
    <div class="super-admin-page">
      <div class="page-header">
        <div>
          <a href="/super-admin/workspaces" class="text-muted">&larr; Back to Workspaces</a>
          <h1 class="mt-2">
            {workspace.slack_team_icon && (
              <img src={workspace.slack_team_icon} alt="" class="workspace-icon-lg" />
            )}
            {workspace.name}
          </h1>
          <p class="text-muted">{workspace.id}</p>
        </div>
      </div>

      <div class="grid-2">
        {/* Workspace Info */}
        <div class="card">
          <div class="card-header">
            <h2>Workspace Info</h2>
          </div>
          <div class="card-body">
            <dl class="info-list">
              <dt>Domain</dt>
              <dd>{workspace.domain || '-'}</dd>

              <dt>Created</dt>
              <dd>{formatDate(workspace.created_at)}</dd>

              <dt>Users</dt>
              <dd>
                {workspace.user_count || 0}
                {sub?.seats_limit && <span class="text-muted"> / {sub.seats_limit} limit</span>}
              </dd>

              <dt>Active Users</dt>
              <dd>{workspace.active_users || 0}</dd>

              <dt>Leave Requests</dt>
              <dd>{workspace.leave_count || 0}</dd>
            </dl>
          </div>
        </div>

        {/* Subscription Info */}
        <div class="card">
          <div class="card-header">
            <h2>Subscription</h2>
            <span class={`badge badge-${getStatusColor(sub?.status)}`}>
              {sub?.status || 'none'}
            </span>
          </div>
          <div class="card-body">
            <dl class="info-list">
              <dt>Plan</dt>
              <dd>{sub?.plan || 'none'}</dd>

              <dt>Trial Started</dt>
              <dd>{formatDate(sub?.trial_started_at)}</dd>

              <dt>Trial Ends</dt>
              <dd>
                {sub?.trial_ends_at ? (
                  <>
                    {formatDate(sub.trial_ends_at)}
                    <span class="text-muted"> ({getDaysRemaining(sub.trial_ends_at)} days)</span>
                  </>
                ) : '-'}
              </dd>

              <dt>Subscription Started</dt>
              <dd>{formatDate(sub?.subscription_started_at)}</dd>

              <dt>Subscription Ends</dt>
              <dd>{formatDate(sub?.subscription_ends_at)}</dd>

              <dt>Billing Email</dt>
              <dd>{sub?.billing_email || '-'}</dd>

              <dt>Stripe Customer</dt>
              <dd>{sub?.stripe_customer_id || '-'}</dd>

              <dt>Discount</dt>
              <dd>
                {sub?.discount_percent > 0 ? (
                  <>
                    <span class="badge badge-info">{sub.discount_percent}%</span>
                    {sub.discount_reason && (
                      <span class="text-muted"> - {sub.discount_reason}</span>
                    )}
                  </>
                ) : 'None'}
              </dd>
            </dl>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div class="card mt-4">
        <div class="card-header">
          <h2>Actions</h2>
        </div>
        <div class="card-body">
          <div class="action-sections">
            {/* Extend Trial */}
            {(sub?.status === 'trialing' || sub?.status === 'expired') && (
              <div class="action-section">
                <h3>Extend Trial</h3>
                <div class="inline-form">
                  <input
                    type="number"
                    min="1"
                    max="90"
                    value={extendDays}
                    onChange={e => setExtendDays(parseInt(e.target.value))}
                    class="input-sm"
                  />
                  <span>days</span>
                  <button
                    class="btn btn-primary"
                    onClick={handleExtendTrial}
                    disabled={actionLoading}
                  >
                    Extend
                  </button>
                </div>
              </div>
            )}

            {/* Activate */}
            {sub?.status !== 'active' && (
              <div class="action-section">
                <h3>Manual Activation</h3>
                <p class="text-muted">Manually activate subscription for 12 months</p>
                <button
                  class="btn btn-success"
                  onClick={handleActivate}
                  disabled={actionLoading}
                >
                  Activate Subscription
                </button>
              </div>
            )}

            {/* Apply Discount */}
            <div class="action-section">
              <h3>Apply Discount</h3>
              <div class="inline-form">
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={discountPercent}
                  onChange={e => setDiscountPercent(parseInt(e.target.value))}
                  class="input-sm"
                  placeholder="0"
                />
                <span>%</span>
                <input
                  type="text"
                  value={discountReason}
                  onChange={e => setDiscountReason(e.target.value)}
                  placeholder="Reason (optional)"
                  class="input-md"
                />
                <button
                  class="btn btn-primary"
                  onClick={handleApplyDiscount}
                  disabled={actionLoading}
                >
                  Apply
                </button>
              </div>
            </div>

            {/* Cancel */}
            {sub?.status === 'active' && (
              <div class="action-section">
                <h3>Cancel Subscription</h3>
                <p class="text-muted">This will immediately cancel the subscription</p>
                <button
                  class="btn btn-danger"
                  onClick={handleCancel}
                  disabled={actionLoading}
                >
                  Cancel Subscription
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Notes */}
      <div class="card mt-4">
        <div class="card-header">
          <h2>Admin Notes</h2>
        </div>
        <div class="card-body">
          <textarea
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder="Add internal notes about this workspace..."
            rows="3"
            class="textarea-full"
          />
          <button
            class="btn btn-primary mt-2"
            onClick={handleUpdateNotes}
            disabled={actionLoading}
          >
            Save Notes
          </button>
        </div>
      </div>

      {/* Recent Events */}
      <div class="card mt-4">
        <div class="card-header">
          <h2>Recent Events</h2>
        </div>
        <div class="card-body">
          {events.length > 0 ? (
            <table class="table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Event</th>
                  <th>Status Change</th>
                  <th>By</th>
                </tr>
              </thead>
              <tbody>
                {events.map(evt => (
                  <tr key={evt.id}>
                    <td>{formatDateTime(evt.created_at)}</td>
                    <td>
                      <span class={`badge badge-${getEventColor(evt.event_type)}`}>
                        {evt.event_type}
                      </span>
                    </td>
                    <td>
                      {evt.old_status && evt.new_status ? (
                        <>
                          <span class="text-muted">{evt.old_status}</span>
                          {' → '}
                          <span>{evt.new_status}</span>
                        </>
                      ) : '-'}
                    </td>
                    <td>{evt.created_by || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p class="text-muted">No events recorded yet</p>
          )}
        </div>
      </div>
    </div>
  );
}

function getStatusColor(status) {
  switch (status) {
    case 'active': return 'success';
    case 'trialing': return 'warning';
    case 'expired':
    case 'cancelled': return 'danger';
    case 'suspended': return 'danger';
    default: return 'secondary';
  }
}

function getEventColor(eventType) {
  if (eventType.includes('activated') || eventType.includes('created')) return 'success';
  if (eventType.includes('extended') || eventType.includes('updated')) return 'info';
  if (eventType.includes('cancelled') || eventType.includes('expired')) return 'danger';
  return 'secondary';
}

function formatDate(dateStr) {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
}

function formatDateTime(dateStr) {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function getDaysRemaining(dateStr) {
  if (!dateStr) return 0;
  const endDate = new Date(dateStr);
  const now = new Date();
  return Math.max(0, Math.ceil((endDate - now) / (1000 * 60 * 60 * 24)));
}
